import React, { memo, useCallback, useContext, useEffect, useMemo } from "react";
import { GenericError, isCurrentSystemThemeDark, isEmpty, useEffectOnce } from "@swiggy-private/core-ui";
import { DeliveryAddressView, InitalLoadingScreen } from "@swiggy-private/payment-ui";

import { PaymentUIHandler } from "./payment-ui-handler";
import PaymentUIWrapper from "./payment-ui-wrapper";
import { options } from "@payment-client/constants/Options";
import { CLEVERTAP_KEYS } from "@payment-client/constants/Clevertap";
import {
    getCanShowDeliveryAddressView,
    getSelectedRewardName,
    hasPaymentMethods,
    splitPaymentGroupsByCouponAndReward,
} from "@payment-client/helpers/PaymentHelper";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { RouteProps } from "@payment-client/interfaces/Router";
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";
import { getDataForDeliveryAddressViewV4 } from "@payment-client/helpers/DeliveryAddressViewHelper";
import Blaze from "@swiggy-private/blaze.js";
import { onPaymentPageImpression, onUpiAppImpression } from "@payment-client/analytics/events/Payment";
import {
    GlobalHeaderState,
    SwgyHeaderKeysList,
    AnalyticsContext,
    DeliveryInfoContext,
    CheckoutRoute,
    useNavigate,
    BLAZE_EVENTS,
    CheckoutContext,
    CheckoutActionTypes,
    ShowPaymentsPayload,
    getBridgeSdk,
} from "@swiggy-private/checkout-core";
import { UpiIntentContext } from "@payment-client/contexts/UpiIntentContext";
import { SplitPayListContext } from "@payment-client/contexts/SplitPayListContext";
import { STATUS_BAR_DEFAULT_COLOR } from "@payment-client/constants/TextDict";

export type PaymentRendererProps = RouteProps;

export const PaymentRenderer = memo(function PaymentRenderer(_: PaymentRendererProps): React.ReactElement {
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const { paymentResponse, loadingPayments, checkoutMetaInfo, reloadPayments } = useContext(ShowPaymentContext);
    const { availableMethods, upiIntentMethodFetchInProgress } = useContext(UpiIntentContext);

    const { deliverySelectedSlot } = useContext(DeliveryInfoContext);

    const {
        amountToPay,
        withCoupon,
        setWithCoupon,
        billTotal,
        withSplitPay,
        selectedSplitPayMethods,
        showSplitPay,
        amountToPayWithoutCouponWithoutSplitpay,
    } = useContext(PaymentPayContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    const { splitPayList } = useContext(SplitPayListContext);

    const [paymentGroups, couponApplicablePaymentGroups, filteredPaymentGroups] = useMemo(
        () => splitPaymentGroupsByCouponAndReward(paymentResponse),
        [paymentResponse],
    );

    const canShowDeliveryAddressView = useMemo(() => getCanShowDeliveryAddressView(), []);

    const showAllMethods = useCallback(() => {
        setWithCoupon(false);
    }, [setWithCoupon]);

    const navigateTo = useNavigate();
    const goToOffersPage = useCallback(() => {
        navigateTo(CheckoutRoute.PAYMENT_OFFERS);
    }, [navigateTo]);

    const showError =
        !loadingPayments && !paymentResponse && (paymentGroups.length === 0 || filteredPaymentGroups.length === 0);

    const showPaymentsPayload = (
        checkoutState.type === CheckoutActionTypes.SHOW_PAYMENTS ? checkoutState.payload : undefined
    ) as ShowPaymentsPayload;

    const showIrctcCart = showPaymentsPayload?.showIrctcCart ?? false;

    useEffectOnce(() => {
        // This is needed to set the payment status bar to be in light theme only temporarily to support scenes cart which will be in dark theme
        const _isCurrentSystemThemeDark = isCurrentSystemThemeDark();
        if (_isCurrentSystemThemeDark) {
            getBridgeSdk()
                .changeStatusBarColor({
                    style: STATUS_BAR_DEFAULT_COLOR,
                    statusBarMode: "default",
                    bottomBarMode: "default",
                })
                .catch(() => void 0);
        }
    });

    useEffect(() => {
        if (showIrctcCart) {
            getBridgeSdk().logClevertapEvent({ eventName: CLEVERTAP_KEYS.IRCTC_PAYMENT_VISIT, params: {} });
            // appsflyer events
            getBridgeSdk().logEvent({ eventName: CLEVERTAP_KEYS.IRCTC_PAYMENT_VISIT, eventParams: {} });
        }
    }, [showIrctcCart]);

    useEffect(() => {
        if (!paymentResponse) {
            return;
        }
        if (showPaymentsPayload?.reload === true) {
            // reseting the state so payment page does not reload again
            setCheckoutState({
                type: CheckoutActionTypes.SHOW_PAYMENTS,
            });
            reloadPayments();
        }

        const cartId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.cartId) ?? "";
        const planId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.planid) ?? "";
        onPaymentPageImpression(impressionEvent, {
            cartId,
            planId,
            couponCode: paymentResponse?.couponCode,
            billTotal,
            withSplitPay,
            couponApplied: paymentResponse?.couponApplied,
        });
    }, [
        billTotal,
        impressionEvent,
        paymentResponse,
        paymentResponse?.couponApplied,
        paymentResponse?.couponCode,
        reloadPayments,
        setCheckoutState,
        showPaymentsPayload?.reload,
        withSplitPay,
    ]);

    useEffect(() => {
        if (!upiIntentMethodFetchInProgress) {
            onUpiAppImpression(impressionEvent, availableMethods, false, true);
        }
    }, [availableMethods, upiIntentMethodFetchInProgress, impressionEvent]);

    if (loadingPayments) {
        return (
            <PaymentUIWrapper withCoupon={withCoupon} setWithCoupon={setWithCoupon}>
                <InitalLoadingScreen />
            </PaymentUIWrapper>
        );
    }

    if (showError || !paymentResponse) {
        return (
            <PaymentUIWrapper withCoupon={withCoupon} setWithCoupon={setWithCoupon}>
                <GenericError action={{ onClick: reloadPayments }} />
            </PaymentUIWrapper>
        );
    }

    const { couponApplied, paymentBannerMessage, presentationDetails, rewardApplied, couponCode, paymentCouponCode } =
        paymentResponse;
    const showCouponGroupsOnly = withCoupon && couponApplied;
    const showRewardGroupsOnly = rewardApplied;
    const showFilteredGroupsOnly = showCouponGroupsOnly || showRewardGroupsOnly;
    const canShowAllMethods = showFilteredGroupsOnly && paymentGroups.length > 0 && hasPaymentMethods(paymentGroups);
    const quantity = Number(paymentResponse.itemQuantity);

    const rewardName = getSelectedRewardName(splitPayList ?? []);

    const deliveryAddressViewData = getDataForDeliveryAddressViewV4({
        checkoutMetaInfo,
        presentationDetails,
        deliverySelectedSlot,
    });
    const couponBannerVisible = showCouponGroupsOnly || !isEmpty(paymentResponse.offerBanner);

    // record payment observed end time
    Blaze.recordEnd(BLAZE_EVENTS.RECORD_CHECKOUT_PAYMENT_LOAD_TIME, {
        mBusinessLine: GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceBusinessLine) || "",
        mCategory: GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceCategory) || "",
    });

    // record dineout payment observed end time
    Blaze.recordEnd(BLAZE_EVENTS.RECORD_DINEOUT_CHECKOUT_PAYMENT_LOAD_TIME);

    return (
        <PaymentUIWrapper
            quantity={quantity}
            amount={billTotal}
            withCoupon={withCoupon}
            setWithCoupon={setWithCoupon}
            couponApplied={couponApplied}
        >
            {canShowDeliveryAddressView && (
                <>
                    {deliveryAddressViewData != null && (
                        <DeliveryAddressView {...deliveryAddressViewData} couponBannerVisible={couponBannerVisible} />
                    )}
                </>
            )}
            <PaymentUIHandler
                options={options}
                paymentGroups={paymentGroups}
                filteredPaymentGroups={filteredPaymentGroups}
                couponApplicablePaymentGroups={couponApplicablePaymentGroups}
                paymentBannerMessage={paymentBannerMessage}
                showAllMethods={showAllMethods}
                canShowAllMethods={canShowAllMethods}
                showCouponGroupsOnly={showCouponGroupsOnly}
                showFilteredGroupsOnly={showFilteredGroupsOnly}
                itemQuantity={quantity}
                banner={paymentResponse.banner}
                splitpayList={splitPayList}
                selectedSplitPayMethods={selectedSplitPayMethods}
                checkoutMetaInfo={checkoutMetaInfo}
                rewardName={rewardName}
                showSplitPay={showSplitPay}
                withSplitPay={withSplitPay}
                amountToPay={amountToPay}
                couponCode={couponCode}
                paymentCouponCode={paymentCouponCode}
                amountToPayWithoutCouponWithoutSplitpay={amountToPayWithoutCouponWithoutSplitpay}
                juspayMeta={paymentResponse.juspayMeta}
                couponApplied={paymentResponse.couponApplied}
                offerStrip={paymentResponse.offerBanner}
                goToOffersPage={goToOffersPage}
            />
        </PaymentUIWrapper>
    );
});
